export const CLASS_LIST_COLUMN_PREFS_KEY = 'enrollio.class-list.column-prefs';

const PINNED_START = ['select', 'title'];
const PINNED_END = ['action'];

export interface ClassListColumnPrefs {
  visibility: Record<string, boolean>;
  order: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function readClassListColumnPrefs(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): ClassListColumnPrefs | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CLASS_LIST_COLUMN_PREFS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const visibility = isRecord(parsed.visibility) ? parsed.visibility : {};
    const order = Array.isArray(parsed.order)
      ? parsed.order.filter((id): id is string => typeof id === 'string')
      : [];
    return {
      visibility: Object.fromEntries(
        Object.entries(visibility).filter(
          (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
        ),
      ),
      order,
    };
  } catch {
    return null;
  }
}

export function writeClassListColumnPrefs(
  prefs: ClassListColumnPrefs,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage,
): void {
  if (!storage) return;
  storage.setItem(CLASS_LIST_COLUMN_PREFS_KEY, JSON.stringify(prefs));
}

export function clearClassListColumnPrefs(
  storage: Pick<Storage, 'removeItem'> | undefined = globalThis.localStorage,
): void {
  storage?.removeItem(CLASS_LIST_COLUMN_PREFS_KEY);
}

export function mergeClassListVisibility(
  saved: Record<string, boolean> | undefined,
  defaults: Record<string, boolean>,
): Record<string, boolean> {
  const next = { ...defaults };
  for (const [id, visible] of Object.entries(saved ?? {})) {
    if (id in defaults && !PINNED_START.includes(id) && !PINNED_END.includes(id)) {
      next[id] = visible;
    }
  }
  for (const id of [...PINNED_START, ...PINNED_END]) {
    if (id in next) next[id] = true;
  }
  return next;
}

export function mergeClassListOrder(
  saved: string[] | undefined,
  columnIds: string[],
): string[] {
  const movable = columnIds.filter(
    (id) => !PINNED_START.includes(id) && !PINNED_END.includes(id),
  );
  const savedMovable = (saved ?? []).filter((id) => movable.includes(id));
  const missing = movable.filter((id) => !savedMovable.includes(id));
  return [
    ...PINNED_START.filter((id) => columnIds.includes(id)),
    ...savedMovable,
    ...missing,
    ...PINNED_END.filter((id) => columnIds.includes(id)),
  ];
}

export function pinColumnAfter(
  order: string[],
  columnId: string,
  afterColumnId: string,
): string[] {
  if (!order.includes(columnId) || !order.includes(afterColumnId)) {
    return order;
  }
  const without = order.filter((id) => id !== columnId);
  const after = without.indexOf(afterColumnId);
  without.splice(after + 1, 0, columnId);
  return without;
}

export function moveColumnInOrder(
  order: string[],
  columnId: string,
  direction: -1 | 1,
): string[] {
  const movable = order.filter(
    (id) => !PINNED_START.includes(id) && !PINNED_END.includes(id),
  );
  const index = movable.indexOf(columnId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= movable.length) {
    return order;
  }
  const reordered = [...movable];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(nextIndex, 0, moved);
  return mergeClassListOrder(
    reordered,
    order,
  );
}
